import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  MenuItem, 
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';

const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();

  const languages = [
    { code: 'en', label: t('language.english', 'English') },
    { code: 'fr', label: t('language.french', 'Français') },
    { code: 'es', label: t('language.spanish', 'Español') }
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    
    // Update URL with new language
    const currentPath = location.pathname;
    
    // Handle URLs both with and without language prefix
    let pathWithoutLang = '';
    const pathParts = currentPath.split('/').filter(part => part);
    
    if (pathParts.length > 0 && ['en', 'fr', 'es'].includes(pathParts[0])) {
      // Path already has language prefix
      pathWithoutLang = pathParts.slice(1).join('/');
    } else {
      // Path doesn't have language prefix
      pathWithoutLang = pathParts.join('/');
    }
    
    navigate(`/${lng}${pathWithoutLang ? '/' + pathWithoutLang : ''}`);
    handleClose();
  };

  // Get current language display name
  const currentLanguage = languages.find(lang => lang.code === i18n.language)?.label || 'English';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={t('language.selectLanguage', 'Select language')}>
        <Button
          id="language-button"
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          endIcon={<KeyboardArrowDownIcon sx={{ color: 'inherit' }} />}
          startIcon={<LanguageIcon sx={{ color: 'inherit' }} />}
          sx={{ 
            textTransform: 'none',
            minWidth: '120px',
            justifyContent: 'space-between',
            fontWeight: 600,
            borderRadius: '20px',
            padding: '6px 12px',
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            }
          }}
        >
          {currentLanguage}
        </Button>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            mt: 1,
            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
            '& .MuiMenuItem-root': {
              py: 1.5
            }
          }
        }}
      >
        {languages.map((language) => (
          <MenuItem 
            key={language.code} 
            onClick={() => changeLanguage(language.code)}
            selected={i18n.language === language.code}
            sx={{
              minWidth: '150px',
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
              }
            }}
          >
            {i18n.language === language.code && (
              <ListItemIcon>
                <CheckIcon fontSize="small" color="primary" />
              </ListItemIcon>
            )}
            <ListItemText 
              primary={language.label} 
              inset={i18n.language !== language.code}
              primaryTypographyProps={{
                fontWeight: i18n.language === language.code ? 'bold' : 'normal',
                fontSize: '0.95rem',
                color: theme.palette.text.primary
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSwitcher;
